import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useConnect, useConnection, useConnectors, useSignMessage, type Connector } from 'wagmi'
import { Button, Dialog, Icon, Spinner, UiLink, cx, shortAddress } from '@algoboo/ui'
import { request, resetSession, errorCopy, isApiError } from '../lib/api'
import { initialSiwe, runSiwe, siweReducer, type SiweState } from '../lib/siwe'
import { INSTALL_LINKS, classifyWalletError, ensureChain, hasInjectedProvider, isMobileDevice, mobileDeepLinks, walletErrorCopy, walletOptions, type Eip1193 } from '../lib/wallet'
import { primaryChain } from '../lib/wagmi'
import { env } from '../env'
import { attempt } from '../lib/attempt'

const STEPS: { key: SiweState['step']; label: string }[] = [
  { key: 'connecting', label: 'Connect wallet' },
  { key: 'nonce', label: 'Preparing sign-in' },
  { key: 'signing', label: 'Confirm in your wallet' },
  { key: 'verifying', label: 'Signing you in' },
]
const ORDER = ['idle', 'connecting', 'network', 'nonce', 'signing', 'verifying', 'done'] as const

const CHAIN_NAMES: Record<number, string> = { 1: 'Ethereum', 10: 'OP Mainnet', 137: 'Polygon', 8453: 'Base', 42161: 'Arbitrum One', 4663: 'Robinhood Chain' }
const chainName = (id: number | undefined) => (id === undefined ? 'another network' : (CHAIN_NAMES[id] ?? 'another network'))

/**
 * Connect → (switch network) → nonce → sign → verify. Reading never needs this; following, creating,
 * mandates, subscriptions and claims do. Wallets come from EIP-6963 announcements (wagmi's discovery), the
 * legacy injected provider, and WalletConnect when a project id is configured. There is no demo path.
 */
export function SiweDialog({ open, onClose, reason }: { open: boolean; onClose: () => void; reason?: string | undefined }) {
  const [state, dispatch] = useReducer(siweReducer, initialSiwe)
  const connectors = useConnectors()
  const { connectAsync } = useConnect()
  const conn = useConnection()
  const { signMessageAsync } = useSignMessage()
  const qc = useQueryClient()
  const running = useRef(false)
  const [pendingUid, setPendingUid] = useState<string | null>(null)
  const [injected, setInjected] = useState(hasInjectedProvider)
  const activeConnector = useRef<Connector | null>(null)

  // closing resets the flow; opening looks for an injected wallet again (adjusted while rendering)
  const [wasOpen, setWasOpen] = useState(open)
  if (wasOpen !== open) {
    setWasOpen(open)
    if (!open) {
      dispatch({ type: 'RESET' })
      setPendingUid(null)
    } else setInjected(hasInjectedProvider())
  }
  useEffect(() => {
    if (!open) running.current = false
  }, [open])

  const start = useCallback(
    async (address: `0x${string}`) => {
      if (running.current) return
      running.current = true
      const ok = await runSiwe(
        address,
        {
          getNonce: (a) => request('siweNonce', { body: { address: a } }),
          sign: (message) => signMessageAsync({ message, account: address }),
          verify: async (message, signature) => {
            const me = await request('siweVerify', { body: { message, signature } })
            await resetSession(qc, me)
          },
          domain: window.location.host,
          uri: window.location.origin,
        },
        dispatch,
      ).catch((err: unknown) => {
        dispatch({ type: 'FAIL', error: isApiError(err) ? errorCopy(err.code).body : String(err) })
        return false
      })
      running.current = false
      if (ok) setTimeout(onClose, 700)
    },
    [signMessageAsync, qc, onClose],
  )

  const switchNetwork = useCallback(
    async (address: `0x${string}`, walletChainId: number | undefined) => {
      const c = activeConnector.current ?? conn.connector
      dispatch({ type: 'SWITCHING' })
      await attempt(
        async () => {
          if (!c) throw new Error('The wallet is no longer connected')
          const provider = (await c.getProvider()) as Eip1193
          await ensureChain(provider, primaryChain, walletChainId)
          void start(address)
        },
        (err) => {
          const kind = classifyWalletError(err)
          dispatch({ type: 'FAIL', error: kind === 'rejected' ? `Your wallet is still on ${chainName(walletChainId)}. Switch networks to continue.` : walletErrorCopy(kind, err), rejected: kind === 'rejected', kind })
        },
      )
    },
    [conn.connector, start],
  )

  /** After connecting (or continuing): check the chain, then sign in. */
  const proceed = useCallback(
    (address: `0x${string}`, chainId: number | undefined) => {
      if (chainId !== undefined && chainId !== env.chainId) {
        dispatch({ type: 'WRONG_NETWORK', address, chainId })
        void switchNetwork(address, chainId)
        return
      }
      dispatch({ type: 'CONNECTED', address })
      running.current = false
      void start(address)
    },
    [start, switchNetwork],
  )

  const connectWith = async (c: Connector) => {
    // wagmi refuses to connect a connector twice ("Connector already connected"): continue with it instead
    if (conn.status === 'connected' && conn.address && conn.connector?.uid === c.uid) {
      activeConnector.current = c
      proceed(conn.address, conn.chainId)
      return
    }
    dispatch({ type: 'CONNECT' })
    setPendingUid(c.uid)
    activeConnector.current = c
    await attempt(
      async () => {
        const res = await connectAsync({ connector: c })
        const addr = res.accounts[0]
        if (!addr) throw new Error('The wallet returned no account (locked?)')
        proceed(addr, res.chainId)
      },
      (err) => {
        const kind = classifyWalletError(err)
        dispatch({ type: 'FAIL', error: kind === 'rejected' ? 'You closed the wallet prompt. Nothing was shared.' : walletErrorCopy(kind, err), rejected: kind === 'rejected', kind })
      },
    )
    setPendingUid(null)
  }

  const connected = conn.status === 'connected' && conn.address ? conn.address : null
  const options = walletOptions(connectors, { hasInjected: injected })
  const mobile = isMobileDevice()
  const listing = state.step === 'idle' || state.step === 'connecting' || (state.step === 'error' && state.failedAt === 'connecting')
  const networkStage = state.step === 'network' || (state.step === 'error' && state.failedAt === 'network')
  const idx = ORDER.indexOf(state.step === 'error' ? (state.failedAt ?? 'idle') : state.step)
  return (
    <Dialog open={open} onClose={onClose} title="Sign in with your wallet" description={reason ?? 'Connect your wallet to continue.'} width={460}>
      <div data-testid="siwe-dialog" data-step={state.step} {...(state.failedAt ? { 'data-failed-at': state.failedAt } : {})} {...(state.kind ? { 'data-error-kind': state.kind } : {})}>
        {state.step === 'error' && (
          <div role="alert" className="mt-4 flex flex-wrap items-start justify-between gap-3 rounded-md bg-danger-soft px-3 py-2.5 text-[13px] text-ink">
            <span className="min-w-0 flex-1">{state.error}</span>
            {state.address && state.failedAt !== 'connecting' && state.failedAt !== 'network' && (
              <Button size="sm" variant="secondary" onClick={() => void start(state.address!)}>
                Try again
              </Button>
            )}
          </div>
        )}

        {listing ? (
          <div className="mt-4 space-y-2">
            {connected && (
              <button
                type="button"
                onClick={() => {
                  activeConnector.current = conn.connector ?? null
                  proceed(connected, conn.chainId)
                }}
                className="rh-press flex min-h-14 w-full items-center gap-3 rounded-lg border border-brand bg-brand-soft px-4 py-3 text-left"
              >
                <Icon name="wallet" size={18} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-semibold text-ink">Continue as {shortAddress(connected)}</span>
                  <span className="block text-[12px] text-ink-3">{conn.connector?.name ?? 'Connected wallet'}</span>
                </span>
                <Icon name="arrow-right" size={16} />
              </button>
            )}
            {options.map((o) => (
              <button
                key={o.connector.uid}
                type="button"
                data-wallet={o.connector.id}
                disabled={state.step === 'connecting'}
                onClick={() => void connectWith(o.connector as Connector)}
                className="rh-press flex min-h-14 w-full items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3 text-left hover:border-line-strong hover:bg-surface-2 disabled:opacity-60"
              >
                {o.icon ? <img src={o.icon} alt="" width={28} height={28} className="h-7 w-7 shrink-0 rounded-md" /> : <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-line text-ink-2"><Icon name={o.kind === 'walletconnect' ? 'chain' : 'wallet'} size={16} /></span>}
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-semibold text-ink">{o.label}</span>
                  <span className="block text-[12px] text-ink-3">{o.hint}</span>
                </span>
                {pendingUid === o.connector.uid ? <Spinner /> : <Icon name="chevron-right" size={16} className="text-ink-3" />}
              </button>
            ))}
            {state.step === 'connecting' && <p className="text-center text-[12.5px] text-ink-3">Waiting for your wallet. If nothing opened, click its icon in the toolbar.</p>}
            {options.length === 0 && <NoWallet mobile={mobile} />}
            {options.length > 0 && mobile && !injected && <DeepLinks />}
          </div>
        ) : networkStage ? (
          <NetworkPanel state={state} onSwitch={() => void switchNetwork(state.address!, state.walletChainId)} />
        ) : (
          <ol className="mt-5 space-y-2.5" aria-live="polite">
            {STEPS.map((s, i) => {
              const pos = ORDER.indexOf(s.key as (typeof ORDER)[number])
              const done = idx > pos || state.step === 'done'
              const active = idx === pos && state.step !== 'error'
              const failed = state.step === 'error' && state.failedAt === s.key
              return (
                <li key={s.key} className="flex items-center gap-3 text-[14px]">
                  <span
                    className={cx(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-mono text-[11px]',
                      done && 'border-gain bg-gain-soft text-gain',
                      active && 'border-brand bg-brand-soft text-accent',
                      failed && 'border-danger bg-danger-soft text-danger',
                      !done && !active && !failed && 'border-line text-ink-3',
                    )}
                  >
                    {done ? <Icon name="check" size={14} strokeWidth={2.4} /> : active ? <Spinner size={12} /> : failed ? <Icon name="close" size={13} /> : i + 1}
                  </span>
                  <span className={cx(done || active ? 'text-ink' : 'text-ink-3', failed && 'text-danger')}>{s.label}</span>
                </li>
              )
            })}
          </ol>
        )}

        {state.step === 'done' && (
          <p role="status" className="mt-4 text-center font-display text-[16px] font-semibold text-gain">
            Signed in. Welcome to the floor.
          </p>
        )}
        <p className="mt-5 border-t border-line pt-3 text-[12px] text-ink-3">
          By signing in you accept the{' '}
          <UiLink href="/terms" onClick={onClose} className="text-ink-2 underline decoration-line-strong underline-offset-2 hover:text-ink">
            terms
          </UiLink>{' '}
          and have read the{' '}
          <UiLink href="/risk" onClick={onClose} className="text-ink-2 underline decoration-line-strong underline-offset-2 hover:text-ink">
            risk disclosure
          </UiLink>
          .
        </p>
      </div>
    </Dialog>
  )
}

function NetworkPanel({ state, onSwitch }: { state: SiweState; onSwitch: () => void }) {
  const failed = state.step === 'error'
  const switching = state.step === 'network' && state.switching
  return (
    <div data-testid="wrong-network" className="mt-4 space-y-3 rounded-lg border border-warn/40 bg-warn-soft p-4">
      <div className="flex items-start gap-3">
        <Icon name="alert" size={18} className="mt-0.5 shrink-0 text-warn" />
        <div className="min-w-0 text-[13.5px] text-ink">
          <p className="font-semibold">Wrong network</p>
          <p className="mt-0.5 text-ink-2">
            Your wallet is on {chainName(state.walletChainId)}. Switch networks to continue.
          </p>
        </div>
      </div>
      {switching ? (
        <p className="flex items-center gap-2 text-[13px] text-ink-2" aria-live="polite">
          <Spinner size={13} /> Confirm the network switch in your wallet.
        </p>
      ) : (
        <Button variant="primary" className="w-full" icon="chain" onClick={onSwitch} disabled={!state.address}>
          {failed ? 'Try switching again' : 'Switch network'}
        </Button>
      )}
    </div>
  )
}

function NoWallet({ mobile }: { mobile: boolean }) {
  return (
    <div data-testid="no-wallet" className="space-y-3 rounded-lg border border-line bg-surface-2 p-4">
      <div>
        <p className="text-[14px] font-semibold text-ink">No wallet found in this browser</p>
        <p className="mt-0.5 text-[13px] text-ink-2">{mobile ? 'Open algo.boo inside your wallet app, or install one.' : 'Install a wallet extension, then reload this page.'}</p>
      </div>
      {mobile && <DeepLinks bare />}
      <ul className="grid grid-cols-2 gap-2">
        {INSTALL_LINKS.map((l) => (
          <li key={l.name}>
            <a href={l.href} target="_blank" rel="noreferrer" className="flex h-11 items-center justify-between gap-2 rounded-md border border-line bg-surface px-3 text-[13px] font-medium text-ink hover:border-line-strong">
              {l.name} <Icon name="external" size={13} className="text-ink-3" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

function DeepLinks({ bare }: { bare?: boolean }) {
  const links = mobileDeepLinks(window.location.href)
  return (
    <div className={cx(!bare && 'pt-2')}>
      {!bare && <p className="mb-2 text-[12px] font-medium uppercase tracking-[0.06em] text-ink-3">Or open in your wallet app</p>}
      <ul className="flex flex-wrap gap-2">
        {links.map((l) => (
          <li key={l.name}>
            <a href={l.href} className="inline-flex h-11 items-center gap-1.5 rounded-md border border-line bg-surface px-3 text-[13px] font-medium text-ink hover:border-line-strong">
              <Icon name="external" size={13} className="text-ink-3" /> {l.name}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}
